import { debounce } from '@luminovo/commons';
import React from 'react';

export interface UseScrollProgressProps {
    ref: React.RefObject<HTMLElement>;
    onScroll?: ({ ratio, absDelta }: { ratio: number; absDelta: number }) => void;
}

/**
 * Important: the onScroll callback must be a stable reference, otherwise the onScroll will
 * never be called.
 *
 * To make the callback stable, use React.useCallback. Example:
 *
 * ```ts
 * const onScroll = React.useCallback(({ ratio, absDelta }) => {
 *    // do something
 * }, []);
 * useScrollProgress({ ref, onScroll });
 * ```
 */
export function useScrollProgress({ ref, onScroll = () => {} }: UseScrollProgressProps): { onScroll: () => void } {
    const debouncedOnScroll = React.useMemo(() => {
        return debounce(onScroll, 200);
    }, [onScroll]);

    const onScrollInner = () => {
        const scrollContainer = ref.current;
        if (!scrollContainer) {
            return;
        }
        const absDelta = scrollContainer.scrollHeight - scrollContainer.clientHeight - scrollContainer.scrollTop;
        const ratio = scrollContainer.scrollTop / (scrollContainer.scrollHeight - scrollContainer.clientHeight);
        debouncedOnScroll({ ratio, absDelta });
    };

    return { onScroll: onScrollInner };
}

export function isNearScrollBottom({ ratio, absDelta }: { ratio: number; absDelta: number }): boolean {
    return ratio > 0.9 || absDelta < 100;
}
